import { useState } from 'react'
import { Plus, Search, MoreVertical, Edit, Trash2, Key, UserCheck, UserX } from 'lucide-react'
import { Button, Badge, Card, Modal, Input, Select } from '../../components/ui'
import type { User } from '../../types'

// Mock data
const mockUsers: User[] = [
  {
    id: 'user-1',
    email: 'admin@example.com',
    name: '管理者',
    role: 'super_admin',
    status: 'active',
    lastLoginAt: '2025-12-01T10:00:00Z',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2025-12-01T00:00:00Z',
  },
  {
    id: 'user-2',
    email: 'yamada@example.com',
    name: '山田太郎',
    role: 'admin',
    status: 'active',
    lastLoginAt: '2025-11-30T15:30:00Z',
    createdAt: '2024-03-15T00:00:00Z',
    updatedAt: '2025-11-30T00:00:00Z',
  },
  {
    id: 'user-3',
    email: 'suzuki@example.com',
    name: '鈴木花子',
    role: 'advertiser',
    advertiserId: 'adv-1',
    status: 'active',
    lastLoginAt: '2025-11-28T09:00:00Z',
    createdAt: '2024-06-01T00:00:00Z',
    updatedAt: '2025-11-28T00:00:00Z',
  },
  {
    id: 'user-4',
    email: 'tanaka@example.com',
    name: '田中一郎',
    role: 'store_manager',
    status: 'active',
    lastLoginAt: '2025-11-25T14:00:00Z',
    createdAt: '2024-08-01T00:00:00Z',
    updatedAt: '2025-11-25T00:00:00Z',
  },
  {
    id: 'user-5',
    email: 'viewer@example.com',
    name: '閲覧者',
    role: 'viewer',
    status: 'inactive',
    createdAt: '2024-10-01T00:00:00Z',
    updatedAt: '2025-10-01T00:00:00Z',
  },
]

const roleConfig = {
  super_admin: { label: 'スーパー管理者', variant: 'danger' as const },
  admin: { label: '管理者', variant: 'primary' as const },
  advertiser: { label: '広告主', variant: 'success' as const },
  store_manager: { label: '店舗管理者', variant: 'warning' as const },
  viewer: { label: '閲覧者', variant: 'gray' as const },
}

export default function UserListPage() {
  const [searchQuery, setSearchQuery] = useState('')
  const [roleFilter, setRoleFilter] = useState('all')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)

  const filteredUsers = mockUsers.filter(user => {
    const matchesSearch = 
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesRole = roleFilter === 'all' || user.role === roleFilter
    return matchesSearch && matchesRole
  })

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">ユーザー管理</h1>
          <p className="text-sm text-gray-500 mt-1">システムユーザーの管理</p>
        </div>
        <Button leftIcon={<Plus className="w-4 h-4" />} onClick={() => setShowCreateModal(true)}>
          新規ユーザー作成
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <div className="p-4 flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="名前・メールアドレスで検索..."
              className="form-input pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <select 
            className="form-select w-48"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <option value="all">すべてのロール</option>
            <option value="super_admin">スーパー管理者</option>
            <option value="admin">管理者</option>
            <option value="advertiser">広告主</option>
            <option value="store_manager">店舗管理者</option>
            <option value="viewer">閲覧者</option>
          </select>
        </div>
      </Card>

      {/* User Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>ユーザー</th>
                <th>ロール</th>
                <th>ステータス</th>
                <th>最終ログイン</th>
                <th className="w-20">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredUsers.map((user) => {
                const role = roleConfig[user.role]
                return (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td>
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-medium">
                          {user.name.charAt(0)}
                        </div>
                        <div className="ml-3">
                          <p className="font-medium text-gray-900">{user.name}</p>
                          <p className="text-sm text-gray-500">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td>
                      <Badge variant={role.variant}>{role.label}</Badge>
                    </td>
                    <td>
                      <Badge variant={user.status === 'active' ? 'success' : 'gray'} dot>
                        {user.status === 'active' ? '有効' : '無効'}
                      </Badge>
                    </td>
                    <td className="text-gray-500">
                      {user.lastLoginAt 
                        ? new Date(user.lastLoginAt).toLocaleString('ja-JP')
                        : '-'
                      }
                    </td>
                    <td>
                      <div className="relative group">
                        <button className="p-2 hover:bg-gray-100 rounded-lg">
                          <MoreVertical className="w-4 h-4 text-gray-500" />
                        </button>
                        <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                          <button 
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                            onClick={() => setEditingUser(user)}
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            編集
                          </button>
                          <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center">
                            <Key className="w-4 h-4 mr-2" />
                            パスワードリセット
                          </button>
                          {user.status === 'active' ? (
                            <button className="w-full px-4 py-2 text-left text-sm text-yellow-600 hover:bg-yellow-50 flex items-center">
                              <UserX className="w-4 h-4 mr-2" />
                              無効化
                            </button>
                          ) : (
                            <button className="w-full px-4 py-2 text-left text-sm text-green-600 hover:bg-green-50 flex items-center">
                              <UserCheck className="w-4 h-4 mr-2" />
                              有効化
                            </button>
                          )}
                          <button className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center">
                            <Trash2 className="w-4 h-4 mr-2" />
                            削除
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showCreateModal || !!editingUser}
        onClose={() => {
          setShowCreateModal(false)
          setEditingUser(null)
        }}
        title={editingUser ? 'ユーザーを編集' : '新規ユーザー作成'}
      >
        <form className="space-y-4">
          <Input
            label="名前"
            required
            defaultValue={editingUser?.name}
            placeholder="山田太郎"
          />
          <Input
            label="メールアドレス"
            type="email"
            required
            defaultValue={editingUser?.email}
            placeholder="example@company.com"
          />
          {!editingUser && (
            <Input
              label="パスワード"
              type="password"
              required
              placeholder="••••••••"
              hint="8文字以上"
            />
          )}
          <Select
            label="ロール"
            required
            defaultValue={editingUser?.role || 'viewer'}
            options={[
              { value: 'super_admin', label: 'スーパー管理者' },
              { value: 'admin', label: '管理者' },
              { value: 'advertiser', label: '広告主' },
              { value: 'store_manager', label: '店舗管理者' },
              { value: 'viewer', label: '閲覧者' },
            ]}
          />
          <div className="flex justify-end gap-3 pt-4">
            <Button 
              variant="secondary" 
              onClick={() => {
                setShowCreateModal(false)
                setEditingUser(null)
              }}
            >
              キャンセル
            </Button>
            <Button type="submit">
              {editingUser ? '更新' : '作成'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}

